<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: AncestorTreeUnified                                   |
| Section: views/trees                                             |
| Purpose: Unified ancestor-tree route with switchable layouts.    |
+------------------------------------------------------------------+
-->
<template>
    <div class="ancestor-unified-view">
        <div class="ancestor-toolbar">
            <div class="ancestor-toolbar__top">
                <h2>
                    {{ $t('treeViews.ancestorsOf', { name: displayName || $t('treeViews.unknown') }) }}
                </h2>
                <button class="ancestor-toolbar__back" @click="goBackToPerson">
                    {{ $t('treeViews.backToProfile') }}
                </button>
            </div>

            <div class="ancestor-toolbar__controls">
                <div class="ancestor-tabs" role="tablist" :aria-label="$t('treeViews.ancestorViewsAria')">
                    <button v-for="option in viewOptions" :key="option.value" class="ancestor-tabs__button"
                        :class="{ active: activeView === option.value }" role="tab"
                        :aria-selected="String(activeView === option.value)" @click="setActiveView(option.value)">
                        {{ option.label }}
                    </button>
                </div>

                <div class="ancestor-generations">
                    <label for="ancestor-generation-select">{{ $t('treeViews.generations') }}</label>
                    <select id="ancestor-generation-select" v-model.number="selectedGenerations"
                        :disabled="activeView === 'web'">
                        <option v-for="count in generationOptions" :key="count" :value="count">
                            {{ count }}
                        </option>
                    </select>
                </div>
            </div>
        </div>

        <div class="ancestor-content" :class="contentClass">
            <component :is="currentComponent" v-bind="currentProps" :key="componentKey" />
        </div>
    </div>
</template>

<script>
import AncestorTreeClassic from '@/views/trees/AncestorTree.vue';
import FamilyTree from '@/views/trees/FamilyTree.vue';
import RadialAncestorChart from '@/views/trees/RadialAncestorChart.vue';
import HorizontalAncestorChart from '@/views/trees/HorizontalAncestorChart.vue';
import AncestorTreePersonCards from '@/views/trees/AncestorTreePersonCards.vue';
import { getAncestors } from '@/api/personService';
import { useLocale } from '@/composables/useLocale';

const VALID_VIEWS = ['table', 'cards', 'web', 'radial', 'horizontal'];

export default {
    name: 'AncestorTreeUnified',
    components: {
        AncestorTreeClassic,
        FamilyTree,
        RadialAncestorChart,
        HorizontalAncestorChart,
        AncestorTreePersonCards
    },
    props: {
        personId: {
            type: String,
            required: true
        }
    },
    setup() {
        const { localePath } = useLocale();
        return { localePath };
    },
    data() {
        return {
            displayName: '',
            activeView: 'table',
            selectedGenerations: 6,
            generationOptions: [3, 4, 5, 6, 7, 8, 9, 10],
        };
    },
    computed: {
        viewOptions() {
            // Labels are resolved at runtime so locale switches update tab text live.
            return [
                { value: 'table', label: this.$t('treeViews.view.table') },
                { value: 'cards', label: this.$t('treeViews.view.cards') },
                { value: 'web', label: this.$t('treeViews.view.web') },
                { value: 'radial', label: this.$t('treeViews.view.radial') },
                { value: 'horizontal', label: this.$t('treeViews.view.horizontal') }
            ];
        },
        currentComponent() {
            // Map UI tab state to concrete component implementations.
            if (this.activeView === 'table') {
                return 'FamilyTree';
            }

            if (this.activeView === 'web') {
                return 'AncestorTreeClassic';
            }

            if (this.activeView === 'cards') {
                return 'AncestorTreePersonCards';
            }

            if (this.activeView === 'horizontal') {
                return 'HorizontalAncestorChart';
            }

            return 'RadialAncestorChart';
        },
        currentProps() {
            // Child components do not share one prop contract, so this adapter keeps
            // view-specific bindings centralized in one computed block.
            if (this.activeView === 'table') {
                return {
                    personID: this.personId,
                    generations: this.selectedGenerations
                };
            }

            if (this.activeView === 'web') {
                return {
                    personId: this.personId
                };
            }

            if (this.activeView === 'cards') {
                return {
                    personId: this.personId,
                    selectedGenerations: this.selectedGenerations
                };
            }

            return {
                personId: this.personId,
                selectedGenerations: this.selectedGenerations
            };
        },
        contentClass() {
            return {
                'ancestor-content--table': this.activeView === 'table',
                'ancestor-content--dark': this.activeView === 'horizontal'
            };
        },
        componentKey() {
            // Force component remount when view/generation changes to avoid stale
            // internal pan/zoom state being reused across chart types.
            return `${this.personId}-${this.activeView}-${this.selectedGenerations}`;
        }
    },
    watch: {
        '$route.query.view': {
            immediate: true,
            handler(nextView) {
                this.activeView = this.normalizeView(nextView);
            }
        }
    },
    async created() {
        await this.fetchRootName();
    },
    methods: {
        normalizeView(viewName) {
            // Backward compatibility for legacy route query naming.
            if (viewName === 'classic') {
                return 'web';
            }

            return VALID_VIEWS.includes(viewName) ? viewName : 'table';
        },
        setActiveView(viewName) {
            if (this.activeView === viewName) {
                return;
            }

            this.activeView = this.normalizeView(viewName);
            this.$router.replace({
                path: this.localePath(`/person/${this.personId}/ancestors`),
                // Keep URL as source of truth so deep links preserve selected view.
                query: { view: this.activeView }
            });
        },
        async fetchRootName() {
            try {
                // generations=0 asks backend for only the root person display fields.
                const person = await getAncestors(this.personId, 0);
                this.displayName = `${person.firstname || ''} ${person.lastname || ''}`.trim() || this.$t('treeViews.unknown');
            } catch (error) {
                console.error('Error loading root person for ancestor view:', error);
            }
        },
        goBackToPerson() {
            this.$router.push(this.localePath(`/person/${this.personId}`));
        }
    }
};
</script>

<style scoped>
.ancestor-unified-view {
    position: relative;
    min-height: 100vh;
}

.ancestor-content {
    min-height: 100vh;
}

.ancestor-content--table {
    padding-top: 124px;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 24px;
    background: url('@/assets/noise128x128.png'), var(--brand-bg-4);
    background-repeat: repeat;
    background-size: auto;
}

.ancestor-content--dark {
    background: url('@/assets/noise128x128.png'), var(--brand-bg-4);
    background-repeat: repeat;
    background-size: auto;
}

.ancestor-toolbar {
    position: fixed;
    left: 24px;
    right: 24px;
    top: 24px;
    z-index: 200;
    padding: 16px;
    border-radius: var(--border-radius-lg);
    background: rgba(24, 24, 24, 0.7);
    backdrop-filter: blur(10px);
    box-shadow: var(--shadow-card-lg);
}

.ancestor-toolbar__top {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: center;
    margin-bottom: 12px;
}

.ancestor-toolbar h2 {
    margin: 0;
    color: var(--brand-text-light);
    font-size: 20px;
    line-height: 1.3;
}

.ancestor-toolbar__back {
    border: 1px solid rgba(255, 255, 255, 0.5);
    background: transparent;
    color: var(--brand-text-light);
    border-radius: var(--border-radius-md);
    padding: 8px 12px;
    font-weight: 700;
    cursor: pointer;
}

.ancestor-toolbar__back:hover {
    background: rgba(255, 255, 255, 0.1);
}

.ancestor-toolbar__controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
}

.ancestor-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.ancestor-tabs__button {
    border: 1px solid rgba(255, 255, 255, 0.35);
    color: var(--brand-text-light);
    border-radius: var(--border-radius-md);
    padding: 8px 12px;
    background: transparent;
    cursor: pointer;
    font-weight: 600;
}

.ancestor-tabs__button.active {
    background: var(--brand-color-1);
    border-color: var(--brand-color-1);
}

.ancestor-generations {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ancestor-generations label {
    color: var(--brand-text-light);
    font-weight: 600;
}

.ancestor-generations select {
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: var(--border-radius-md);
    background: rgba(0, 0, 0, 0.24);
    color: var(--brand-text-light);
    padding: 8px 10px;
}

.ancestor-generations select:disabled {
    opacity: 0.5;
}

@media (max-width: 768px) {
    .ancestor-toolbar {
        left: 12px;
        right: 12px;
        top: 12px;
        padding: 12px;
    }

    .ancestor-toolbar h2 {
        font-size: 16px;
    }

    .ancestor-toolbar__top {
        flex-direction: column;
        align-items: flex-start;
    }

    .ancestor-content--table {
        padding-top: 112px;
        padding-left: 12px;
        padding-right: 12px;
        padding-bottom: 12px;
    }
}
</style>
